import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import constants from 'scoreboard/components/constants'
import template from './widget-empty-state.hbs'
import Tooltip from 'scoreboard/components/tooltip'
import ErrorView from 'scoreboard/components/error-view'
import appDisplay from 'scoreboard/components/app-display'
import layoutManager from 'scoreboard/components/layout-manager'

const WidgetEmptyState = Backbone.View.extend({

	events: {
		'mouseenter': 'showEmptyStateTooltip',
		'mouseleave': 'removeEmptyStateTooltip',
		'click': 'onClick'
	},

	/**
	 * Renders a widget's "empty" state, handling things like missing permissions
	 * or server errors. Will render the message in the widget if there is room,
	 * or in a tooltip that appears on mouseover if not.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {jQuery} options.container The jQuery-wrapped DOM node which will
	 * be used for sizing and showing/hiding content appropriately
	 *
	 * @param {String} [options.message] The user-friendly error message to
	 * display to the user. i18n will not be applied, so it should be pre-localized.
	 * It should also be escaped. If omitted, will attempt to use options.errorResponse
	 * for the message instead
	 *
	 * @param {Object} [options.errorResponse] The response from the server containing
	 * the error (ResolvableExceptionResponse). Object expected to include properties
	 * like `messageCode`, `arguments`, and `stackTrace`
	 *
	 * @param {String} options.widgetType The type of the containing widget. Valid values include
	 * 'table', 'filter', 'value', 'old-chart'	 *
	 *
	 * @param {String} [options.icon='widget-empty-state'] ID of the icon to use
	 *
	 * @param {Function} [options.onClick] override the default error message display
	 *
	 * @param {Boolean} [options.showErrorTooltip=true] True if the error tooltip should be shown on hover,
	 * else, false.
	 *
	 * @param {Boolean} [options.hasStandardIconSize=true] True if the icon should
	 * have the standard dimensions regardless of container size.
	 *
	 * @param {Boolean} [options.forceIsShort=false] True if the widget should
	 * be forced into a shorter height, typically for error states.
	 */

	initialize: function(options) {
		this.options = options || {}

		this.message = this.options.message
		this.showErrorTooltip = _.defaultTo(this.options.showErrorTooltip, true)

		if (!this.message && options.errorResponse) {
			this.message = i18n.get(options.errorResponse.messageCode, options.errorResponse.arguments)
		}
	},

	render: function() {
		this.options.container.on('resized', () => this.emptyStateLayout())

		let typesToAddResultContainer = ['table', 'filter', 'value',
			'chart', 'chart-table', 'shape', 'chart-color-legend',
			'linear-gauge', 'speedometer', 'report']

		let errorMessageCode = _.get(this.options.errorResponse, 'messageCode')

		let resultsContainer
		if (typesToAddResultContainer.includes(this.options.widgetType)) {
			resultsContainer = this.$el.empty().addClass('results-container')
		} else {
			resultsContainer = this.$('.results-container')
		}

		let icon
		if (this.options.icon) {
			icon = this.options.icon
		} else if (errorMessageCode === 'report.designer.datasetError') {
			icon = 'dataset-has-error'
		} else {
			icon = 'widget-empty-state'
		}

		resultsContainer
			.addClass('widget-empty-state')
			.toggleClass('has-stack-trace', !!_.get(this.options.errorResponse, 'stackTrace'))
			.html(template({
				message: this.message,
				icon: icon
			}))
		this.emptyStateIcon = this.$('.icon-empty-state')
		this.emptyStateLayout()

		return this
	},

	emptyStateLayout() {
		let widgetHeight = this.$el.height()
		let isShort = widgetHeight <= 160
			&& (!this.options.hasStandardIconSize
			|| this.options.forceIsShort === true)
		this.$el.toggleClass('is-short', isShort)

		// If widget `is-short`, apply new height/width to icon. Otherwise, remove inline styles
		// Limit the icon max-height to the icon's default height set via CSS of 76px.
		let iconDimensions = isShort ? Math.min(76, Math.max(25, widgetHeight - constants.STANDARD_MARGIN)) : ''
		this.emptyStateIcon.css({'height': iconDimensions, 'width' : iconDimensions})
	},

	showEmptyStateTooltip() {
		if (this.showErrorTooltip && this.$el.hasClass('is-short')) {
			this.emptyStateTooltipMessage = new Tooltip({
				attachTo: this.el,
				content: this.$('.empty-state-text').text(),
				arrow: 'center',
				ignoreAttachToClick: true,
				placement: 'above',
				attachToCener: false
			}).render()
		}
	},

	removeEmptyStateTooltip() {
		// Check if the tooltip message for the empty state is not null or undefined
		if (!_.isNil(this.emptyStateTooltipMessage)) {
			this.emptyStateTooltipMessage.remove()
			delete this.emptyStateTooltipMessage
		}
	},

	onClick() {
		if (this.options.onClick) {
			this.options.onClick()
			return
		}

		if (this.options.errorResponse && !layoutManager.sharedResource) {
			appDisplay.dialog(new ErrorView(this.options.errorResponse))
		}
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.emptyStateTooltipMessage], 'remove')
	}

})

export default WidgetEmptyState


WidgetEmptyState.__filename__ = 'dashboards/widgets/widget-empty-state.js'
